import { ClipboardList, PenTool, ArrowUpRight } from "lucide-react";
import { Reveal, SectionTag, SectionHeading } from "./Reveal";
import { ASSETS } from "../lib/site";
import { scrollToId } from "../lib/scroll";

const SERVICES = [
  {
    icon: ClipboardList,
    title: "Order Management",
    tag: "Live · Real-Time",
    body: "A dedicated order specialist runs your desk in real time — order entry, vendor follow-ups, RAs and backorders, proof dispatch, and tracking updates, all while you focus on selling.",
    bullets: ["Live order entry & processing", "Vendor chasing, RAs & backorders", "Proof dispatch & approvals", "Tracking & status updates"],
    image: ASSETS.promoMerch,
    testId: "service-card-order-management",
  },
  {
    icon: PenTool,
    title: "Graphic Design",
    tag: "Print-Ready",
    body: "Professional design for promotional product decoration and branding — vector artwork, virtual samples, and marketing collateral, delivered same-day.",
    bullets: ["Vector artwork & redraws", "Virtual samples & mockups", "Flyers, catalogs & brand assets"],
    image: ASSETS.designerAtWork,
    testId: "service-card-graphic-design",
  },
];

const spotlight = (e) => {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty("--x", `${e.clientX - r.left}px`);
  el.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const Services = () => (
  <section id="services" className="relative bg-white py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionTag index="02" label="Core Services" testId="services-section-tag" />
      <div className="mt-6 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="max-w-2xl">
          <SectionHeading>Two services. One seamless operation.</SectionHeading>
        </div>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-slate-500">
            Dedicated per-employee teams that plug directly into your workflow — no
            ticket queues, no shared pools, no waiting.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {SERVICES.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.12}>
            <article
              data-testid={s.testId}
              onMouseMove={spotlight}
              className="spotlight-card group relative h-full rounded-2xl border border-slate-200 bg-white transition-transform duration-500 hover:-translate-y-1.5"
            >
              <div className="relative h-52 overflow-hidden rounded-t-2xl sm:h-60">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" />
                <span className="absolute left-6 top-5 rounded-full border border-[#6DC8F0]/30 bg-white/85 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.22em] text-[#2E3192] backdrop-blur">
                  {s.tag}
                </span>
              </div>

              <div className="relative p-7 sm:p-9">
                <div className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#2E3192]/10 text-[#2E3192]">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">{s.title}</h3>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-500 sm:text-base">{s.body}</p>
                <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-xs text-slate-600 sm:text-sm">
                      <span className="h-1 w-1 rounded-full bg-[#6DC8F0]" />
                      {b}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => scrollToId("contact")}
                  className="mt-8 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#2E3192] transition-colors hover:text-slate-900"
                >
                  Request a quote
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Services;
